import {
	confirmResetPassword,
	confirmSignIn,
	confirmSignUp,
	fetchMFAPreference,
	getCurrentUser,
	resendSignUpCode,
	resetPassword,
	signIn,
	signOut,
	signUp,
	updateMFAPreference,
} from "aws-amplify/auth";
import type { Credentials } from "@/core/types/credentials.type";
import { MFA, type MfaType } from "@/core/types/mfa.type";
import type { User } from "@/core/types/user.type";

export const userConfirmation = async ({
	username,
	code,
}: {
	username: string;
	code: string;
}) => {
	const { isSignUpComplete, nextStep } = await confirmSignUp({
		username,
		confirmationCode: code,
	});

	return { isSignUpComplete, nextStep };
};

export const userVerification = async ({ code }: { code: string }) => {
	const { isSignedIn, nextStep } = await confirmSignIn({
		challengeResponse: code,
	});

	return { isSignedIn, nextStep };
};

export const getMfaPreference = async (): Promise<MfaType | undefined> => {
	const { preferred } = await fetchMFAPreference();

	switch (preferred) {
		case "TOTP":
			return MFA.TOTP;
		case "SMS":
			return MFA.SMS;
		case "EMAIL":
			return MFA.EMAIL;
		default:
			return undefined;
	}
};

export const resendUserConfirmation = async ({
	username,
}: {
	username: string;
}) => {
	const { destination, deliveryMedium } = await resendSignUpCode({
		username,
	});

	return { destination, deliveryMedium };
};

export const setSignInMfaPreference = async (mfa: MfaType) => {
	const { isSignedIn, nextStep } = await confirmSignIn({
		challengeResponse: mfa,
	});

	return { isSignedIn, nextStep };
};

export const verifyTotpSetupCode = async (code: string) => {
	const { isSignedIn, nextStep } = await confirmSignIn({
		challengeResponse: code,
	});

	return { isSignedIn, nextStep };
};

export const setDefaultMfaPreference = async (mfa: MfaType) => {
	return await updateMFAPreference({
		email: mfa === MFA.EMAIL ? "PREFERRED" : undefined,
		sms: mfa === MFA.SMS ? "PREFERRED" : undefined,
		totp: mfa === MFA.TOTP ? "PREFERRED" : undefined,
	});
};

export const userResetPassword = async ({ email }: { email: string }) => {
	const { nextStep } = await resetPassword({ username: email });
	return { nextStep };
};

export const userConfirmResetPassword = async ({
	email,
	code,
	newPassword,
}: {
	email: string;
	code: string;
	newPassword: string;
}) => {
	await confirmResetPassword({
		username: email,
		confirmationCode: code,
		newPassword,
	});
};

export const userSignIn = async ({ email, password }: Credentials) => {
	// If someone is already signed in, only sign them out when a *different*
	// user is signing in (e.g. switching accounts). This avoids an unnecessary
	// network round-trip on every login and avoids wiping an in-flight
	// challenge session (first-time password, MFA) during a retry.
	try {
		const currentUser = await getCurrentUser();
		const currentLoginId =
			currentUser.signInDetails?.loginId ?? currentUser.username;

		if (currentLoginId?.toLowerCase() !== email.toLowerCase()) {
			await signOut();
		}
	} catch {
		// getCurrentUser throws when no one is signed in — nothing to clear.
	}

	const { isSignedIn, nextStep } = await signIn({
		username: email,
		password,
		options: {
			authFlowType: "USER_AUTH",
			preferredChallenge: "PASSWORD_SRP",
		},
	});

	return { isSignedIn, nextStep };
};

export const userSignOut = async () => {
	await signOut({ global: true });
};

export const userSignUp = async (user: Omit<User, "id">) => {
	const { email, given_name, family_name, password, phone_number } = user;

	const { isSignUpComplete, nextStep, userId } = await signUp({
		username: email,
		password,
		options: {
			userAttributes: {
				email,
				phone_number: phone_number,
				given_name: given_name,
				family_name: family_name,
			},
		},
	});

	return { isSignUpComplete, nextStep, userId };
};
